import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  useSquadCreateControllerExecuteMutation,
  useSquadAgentUpdateControllerExecuteMutation,
  useSquadAgentDeleteControllerExecuteMutation,
  AgentDto,
} from '@shared/services/apiService/apiService';
import { SQUAD_MESSAGES } from '../constants/squad.constants';
import { isErrorResponse } from '../utils/squadErrors';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';

export const useSquadManagement = (
  campaignId: string | undefined,
  squadId: string | null,
  agents: AgentDto[],
  onRefresh?: () => void,
) => {
  const { t } = useTranslation();
  const { showError } = useErrorHandler();
  const [defaultAgentId, setDefaultAgentId] = useState<string | null>(null);

  const [createSquad, { isLoading: isCreatingSquad }] = useSquadCreateControllerExecuteMutation();
  const [updateSquadAgent, { isLoading: isUpdatingAgent }] =
    useSquadAgentUpdateControllerExecuteMutation();
  const [deleteSquadAgent, { isLoading: isDeletingAgent }] =
    useSquadAgentDeleteControllerExecuteMutation();

  useEffect(() => {
    if (squadId && agents.length > 0) {
      const defaultAgent = agents.find((agent: any) => agent.isDefault === true);
      if (defaultAgent) {
        setDefaultAgentId(defaultAgent.id);
      } else if (!defaultAgentId) {
        setDefaultAgentId(agents[0].id);
      }
    }
  }, [squadId, agents, defaultAgentId]);

  const handleCreateSquad = useCallback(
    async (data: { name: string; description: string }) => {
      if (!campaignId) return;

      try {
        const response = await createSquad({
          id: campaignId,
          squadCreateRequestDto: {
            name: data.name,
            description: data.description,
          },
        });

        if (isErrorResponse(response)) {
          showError({
            title: 'Create Squad Failed',
            message: t(SQUAD_MESSAGES.CREATED_ERROR),
            suggestion: 'Please check your input and try again',
          });
          return { success: false };
        }

        // Show success toast
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: t('adminMainPage.squad.messages.success'),
            description: t(SQUAD_MESSAGES.CREATED_SUCCESS, { name: data.name }),
          },
        });

        if (onRefresh) {
          onRefresh();
        }

        return { success: true };
      } catch (error) {
        showError({
          title: 'Create Squad Failed',
          error,
          suggestion: 'Please check your input and try again',
        });
        return { success: false };
      }
    },
    [campaignId, createSquad, t, onRefresh, showError],
  );

  const handleSetAsDefault = useCallback(
    async (agent: AgentDto) => {
      if (!squadId) return { success: false };

      try {
        await updateSquadAgent({
          id: squadId,
          agentId: agent.id,
          squadAgentUpdateRequestDto: {
            // Include LLM fields as they are required by backend
            llmProviderId: agent.llmProviderId,
            llmModelId: agent.llmModelId ?? null,
            llmCustomModelKey: agent.llmCustomModelKey ?? null,
            name: agent.name,
            voiceId: agent.voiceId,
            transcriberId: agent.transcriberId,
            profileId: agent.profileId,
            languages: agent.languages || [],
            isDefault: true,
          },
        });

        // Show success toast
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: t('adminMainPage.squad.messages.success'),
            description: t(SQUAD_MESSAGES.SET_DEFAULT_SUCCESS, { name: agent.name }),
          },
        });

        if (onRefresh) {
          await onRefresh();
        }

        return { success: true };
      } catch (error) {
        showError({
          title: 'Set Default Agent Failed',
          error,
          suggestion: 'Please refresh the page and try again',
        });
        return { success: false };
      }
    },
    [squadId, updateSquadAgent, t, onRefresh, showError],
  );

  const handleDeleteSquadAgent = useCallback(
    async (agentId: string) => {
      if (!squadId) return { success: false };

      try {
        const response = await deleteSquadAgent({
          id: squadId,
          agentId,
        });

        if (isErrorResponse(response)) {
          showError({
            title: 'Delete Agent Failed',
            message: t(SQUAD_MESSAGES.AGENT_DELETED_ERROR),
            suggestion: 'Please refresh the page and try again',
          });
          return { success: false };
        }

        const data = response.data;
        if (data && 'defaultAgentId' in data && data.defaultAgentId) {
          setDefaultAgentId(data.defaultAgentId);
        }

        // Show success toast
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: t('adminMainPage.squad.messages.success'),
            description: t(SQUAD_MESSAGES.AGENT_DELETED_SUCCESS),
          },
        });

        if (onRefresh) {
          onRefresh();
        }

        return { success: true };
      } catch (error) {
        showError({
          title: 'Delete Agent Failed',
          error,
          suggestion: 'Please refresh the page and try again',
        });
        return { success: false };
      }
    },
    [squadId, deleteSquadAgent, t, onRefresh, showError],
  );

  return {
    defaultAgentId,
    setDefaultAgentId,
    createSquad: handleCreateSquad,
    setAsDefault: handleSetAsDefault,
    deleteSquadAgent: handleDeleteSquadAgent,
    isCreatingSquad,
    isUpdatingAgent,
    isDeletingAgent,
    isLoading: isCreatingSquad || isUpdatingAgent || isDeletingAgent,
  };
};
